import type { ReactNode } from "react";

export type AnswerFact = {
  /** Short label, e.g. "Price range" / "দামের রেঞ্জ". */
  label: string;
  /** Concrete value — a number, a range, a timeframe. Never a marketing phrase. */
  value: ReactNode;
};

/**
 * A short, factual "quick answer" block placed directly under a page hero.
 *
 * Purpose is editorial, not decorative: search engines and AI answer engines
 * lift the first concrete paragraph that answers the page's implied question
 * ("how much does X cost in Bangladesh?"). Everything rendered here must come
 * from live data on the page — never a placeholder, never a claim we cannot
 * back with the listings shown below it.
 *
 * The block reads on the dark `--color-ink` surfaces used by the category,
 * brand and catalogue pages; it inherits the current text colour so it also
 * works on light surfaces.
 */
export function AnswerBlock({
  question,
  answer,
  facts,
  footnote,
}: {
  question: string;
  answer: ReactNode;
  facts?: AnswerFact[];
  footnote?: ReactNode;
}) {
  return (
    <section
      aria-label={question}
      className="mt-8 rounded-2xl border border-current/15 bg-current/[0.04] p-5 md:p-6"
    >
      <h2 className="text-sm md:text-base font-extrabold leading-snug">{question}</h2>
      <p className="mt-2 text-sm md:text-[15px] leading-relaxed opacity-80 max-w-3xl">{answer}</p>
      {facts && facts.length > 0 ? (
        <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.label} className="min-w-0">
              <dt className="text-[11px] font-semibold opacity-55">{f.label}</dt>
              <dd className="mt-0.5 text-sm font-bold break-words">{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {footnote ? <p className="mt-4 text-xs opacity-55">{footnote}</p> : null}
    </section>
  );
}
